/* eslint-disable no-param-reassign -- immer draft mutations in Zustand */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { useConnectorStore } from './connectorStore';
import { useDesignUIStore } from './designUIStore';
import { useActionHistoryStore } from '../components/FlowCanvas/stores/actionHistoryStore';

import { Connector } from '~/scenes/Build/helpers';

export type TriggerConfig = {
  type: string | undefined;
  name: string | undefined;
  config: Record<string, unknown> | null;
  category?: string;
};

type TriggerState = {
  trigger: TriggerConfig;
  shownTriggerType: Connector | null;
  isTriggerConfigOpen: boolean;
  scopes: string[];
};

type TriggerActions = {
  setTrigger: (trigger: TriggerConfig) => void;
  setTriggerScopes: (scopes: string[]) => void;
  setShownTriggerType: (shownTriggerType: Connector | null) => void;
  setIsTriggerConfigOpen: (isOpen: boolean) => void;
  setTriggerConfig: (config: Record<string, unknown> | null) => void;
  setTriggerName: (name: string | undefined) => void;
  setTriggerType: (type: string | undefined) => void;
  setTriggerCategory: (category: string | undefined) => void;
  reset: () => void;
};

type TriggerStore = TriggerState & TriggerActions;

const initialTrigger: TriggerConfig = {
  type: undefined,
  name: undefined,
  config: null
};

const initialState: TriggerState = {
  trigger: initialTrigger,
  shownTriggerType: null,
  isTriggerConfigOpen: false,
  scopes: []
};

export const useTriggerStore = create<TriggerStore>()(
  devtools(
    immer(set => ({
      ...initialState,

      setTrigger: trigger => set({ trigger }),

      setTriggerScopes: scopes => set({ scopes }),

      setShownTriggerType: shownTriggerType => set({ shownTriggerType }),

      setIsTriggerConfigOpen: isTriggerConfigOpen =>
        set({ isTriggerConfigOpen }),

      setTriggerConfig: config =>
        set(state => {
          state.trigger.config = config;
        }),

      setTriggerName: name =>
        set(state => {
          state.trigger.name = name;
        }),

      setTriggerType: type =>
        set(state => {
          state.trigger.type = type;
        }),

      setTriggerCategory: category =>
        set(state => {
          state.trigger.category = category;
        }),

      reset: () => set({ ...initialState })
    })),
    { name: 'TriggerStore' }
  )
);

export const setTrigger = (trigger: TriggerConfig) =>
  useTriggerStore.getState().setTrigger(trigger);

export const setTriggerScopes = (scopes: string[]) =>
  useTriggerStore.getState().setTriggerScopes(scopes);

export const setShownTriggerType = (shownTriggerType: Connector | null) =>
  useTriggerStore.getState().setShownTriggerType(shownTriggerType);

export const setIsTriggerConfigOpen = (isOpen: boolean) => {
  if (isOpen) {
    useActionHistoryStore.getState().disableUndoRedo('trigger-config');
  } else {
    useActionHistoryStore.getState().enableUndoRedo('trigger-config');
  }

  useTriggerStore.getState().setIsTriggerConfigOpen(isOpen);
};

export const setTriggerConfig = (config: Record<string, unknown> | null) =>
  useTriggerStore.getState().setTriggerConfig(config);

export const setTriggerName = (name: string | undefined) =>
  useTriggerStore.getState().setTriggerName(name);

export const setTriggerType = (type: string | undefined) =>
  useTriggerStore.getState().setTriggerType(type);

export const setTriggerCategory = (category: string | undefined) =>
  useTriggerStore.getState().setTriggerCategory(category);

export const resetTriggerStore = () => useTriggerStore.getState().reset();

export const handleOpenTriggerConfig = () => {
  const { isCapsule } = useDesignUIStore.getState();
  if (isCapsule) return;

  const currentTriggerSpec = useTriggerStore.getState().trigger;
  if (currentTriggerSpec.name) {
    const triggerType = useConnectorStore
      .getState()
      .connectors?.triggers?.find(t => t.name === currentTriggerSpec.name);
    setShownTriggerType(triggerType || null);
  }
  setIsTriggerConfigOpen(true);
};
